<script setup lang="ts">
import CheckpointAutocomplete from '@/components/Filters/components/CheckpointAutocomplete.vue'
import EventAutocomplete from '@/components/Filters/components/EventAutocomplete.vue'
import checkpointApi from '@/services/checkpoint.service'
import store from '@/store'
import { EFiltersActions } from '@/store/filtersStore/FiltersStoreTypes'
import { EStoreModules } from '@/store/storeType'
import type { EventCheckpointType } from '@/types/filters/EventCheckpointType'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import type { AssociatedDeviceModel } from '@/types/models/DeviceModel'
import type { EventModel } from '@/types/models/EventModel'
import { EPOIPerm } from '@/types/permissions/CheckpointsPermissionsEnum'
import { ESitesPerm } from '@/types/permissions/EventsPermissionsEnum'
import { computed, ref, watch } from 'vue'

const permissions = computed(() => store.state.authentication?.me?.permissions ?? [])
const userHasSitePermission = computed(
    () => permissions.value?.find(item => item === ESitesPerm.EVENTS_VIEW)
)
const userHasPoiPermission = computed(
    () => permissions.value?.find(item => item === EPOIPerm.CHECKPOINTS_VIEW)
)

type Props = EventCheckpointType & {
    keepEventInStorage?: boolean
    filterAllEvents?: boolean
    hideEventSelector?: boolean
}
const props = defineProps<Props>()
type Emits = {
    (e: 'change', value: Omit<EventCheckpointType, 'event'>): void
    (e: 'selectEvent', value?: EventModel): void
    (e: 'associates', value?: AssociatedDeviceModel[]): void
    (e: 'selectCheckpoint', value?: CheckpointModel): void
}
const emit = defineEmits<Emits>()

const handleSelect = (value: EventCheckpointType) =>
    emit('change', { ...value, device_id: undefined })

const handleSelectEvent = (value: EventModel | undefined) => {
    if (props.keepEventInStorage)
        store.dispatch(
            `${EStoreModules.FILTERS}/${EFiltersActions.SET_FILTER}`,
            value
                ? {
                      event: { id: value.id, name: value.name }
                  }
                : undefined
        )
    handleSelect({ site_id: value?.id, poi_id: undefined })
    checkpointInputValue.value = undefined
    checkpointOptions.value = []
    emit('selectEvent', value)
    emit('selectCheckpoint', undefined)
}
const handleSelectCheckpoint = (value: CheckpointModel | undefined) => {
    handleCheckpointTyping(undefined)
    handleSelect({ poi_id: value?.id })
    emit('selectCheckpoint', value)
    emit('associates', value?.associates)
}

const getCheckpoints = (site_id?: number, name?: string) => {
    if (!site_id) return
    checkpointApi
        .getCheckpointsFromEvent(site_id, { per_page: -1, filters: name ? { name } : undefined })
        .then(res => {
            checkpointOptions.value = res.data ?? res
            checkpointInputOptions.value = undefined
            checkpointInputValue.value = undefined
        })
}

const checkpointOptions = ref<CheckpointModel[]>()

const handleCheckpointTyping = (value?: string) => {
    checkpointInputValue.value = value
    checkpointInputOptions.value = checkpointOptions.value?.filter(item =>
        item.name.toLocaleLowerCase().includes(value?.toLowerCase() ?? '')
    )
}
const checkpointInputValue = ref<string>()
const checkpointInputOptions = ref<CheckpointModel[]>()
watch(
    () => props.event,
    newValue => {
        newValue?.id ? getCheckpoints(newValue?.id) : undefined
    },
    { immediate: true }
)
</script>

<template>
    <EventAutocomplete
        v-if="userHasSitePermission && !hideEventSelector"
        :event="event"
        :siteId="site_id"
        :filterAllEvents="filterAllEvents"
        @change="(value, event) => handleSelectEvent(value)"
        required
        class="full-width" />
    <CheckpointAutocomplete
        v-if="userHasPoiPermission"
        :checkpointOptions="checkpointInputOptions ?? checkpointOptions ?? []"
        :poiId="poi_id"
        :checkpoint="checkpoint"
        :inputValue="checkpointInputValue"
        @typing="handleCheckpointTyping"
        @select="(value, event) => handleSelectCheckpoint(value)"
        class="full-width" />
</template>
